/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { ContentBlock, FlexCol, FlexRow, Stack, useFlags } from 'sarsaparilla';
import Progress from '../IAOrderDetailsPage/Progress';
import HeaderPlaceholder from '../IAOrderDetailsPage/HeaderPlaceholder';
import SummaryPlaceholder from '../IAOrderDetailsPage/SummaryPlaceholder';

export default function PassFeatureFlag({
    flag,
    flagValueToMatch = true,
    fallback,
    skeleton,
    children,
}) {
    const flags = useFlags();
    const steps = ['Pass Selection', 'Shopping Cart', 'Payment', 'Confirmation'];

    if (isEmpty(flags)) {
        if (skeleton) {
            return (
                <main id="page-body" className="ia-order-details-page">
                    <Progress activeStep={1} steps={steps} />
                    <ContentBlock className="mt-5 mb-5">
                        <Stack>
                            <FlexRow>
                                <FlexCol lg={8}>
                                    <HeaderPlaceholder />
                                </FlexCol>
                                <FlexCol lg={4} className="ia-order-details-summary">
                                    <SummaryPlaceholder />
                                </FlexCol>
                            </FlexRow>
                        </Stack>
                    </ContentBlock>
                </main>
            );
        }
        return <div />;
    }

    if (flags[flag] === flagValueToMatch) {
        return <>{children}</>;
    }

    return <>{fallback}</> || null;
}

PassFeatureFlag.propTypes = {
    /** The LaunchDarkly flag to be evaluated. Flags are converted to camel
     * case, so "my-nice-flag" will be "myNiceFlag" in the React app */
    flag: PropTypes.string.isRequired,

    /** The content/component to be rendered if the flag's value evaluates to true */
    children: PropTypes.node.isRequired,

    /** Optional content/component rendered if the flag's value evaluates to false */
    fallback: PropTypes.node,

    /** The value to compare to the flag value coming from LaunchDarkly. Uses
     * strict equality. Use this to override the default of a boolean-type
     * flag where "true" means the feature is on and children should render */
    flagValueToMatch: PropTypes.any,

    // Allows to add a skeleton loading structure
    skeleton: PropTypes.bool,
};
